/**
 * Student Life360 — behaviour, wellbeing, pastoral care, clubs, leadership,
 * merits, demerits and guardian communication. All KPIs flow through the
 * Central Metrics Service.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import {
  Award, HeartHandshake, Trophy, Users, ShieldCheck, Sparkles,
  Plus, X, Loader2, ClipboardList, AlertTriangle, GraduationCap, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, KPITile, Section, Loading, StatusPill, DataTable, EmptyState } from "../components/Common";

const KIND_TONE = {
  merit: "positive", achievement: "positive", commendation: "positive",
  service_hour: "positive", demerit: "major", reprimand: "major",
};

const KINDS = ["merit", "achievement", "commendation", "service_hour", "demerit", "reprimand"];
const ACTIVITY_KINDS = ["sport", "club", "society", "music", "drama", "debate", "stem", "entrepreneurship"];
const AWARD_CATEGORIES = ["academic", "sport", "music", "drama", "leadership", "service", "character", "attendance"];
const PASTORAL_KINDS = ["counselling", "wellness_check", "medical_visit", "psychology_note", "concern"];
const CATEGORIES = ["general", "punctuality", "uniform", "effort", "kindness", "leadership", "sports", "academic"];

const LEADERSHIP_ROLES = ["Head Boy", "Head Girl", "Deputy Head Boy", "Deputy Head Girl", "Prefect", "House Captain", "Vice Captain", "SRC Representative", "Sports Captain"];

function Tab({ id, active, onClick, icon: Icon, label, count }) {
  return (
    <button
      onClick={onClick}
      data-testid={`tab-${id}`}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-md border transition ${
        active ? "bg-[color:var(--hl-navy)] text-white border-[color:var(--hl-navy)] shadow-sm" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
      }`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
      {count !== undefined && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${active ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>{count}</span>
      )}
    </button>
  );
}

function EventForm({ students, onClose, onSaved }) {
  const [form, setForm] = useState({
    student_id: students[0]?.id || "",
    kind: "merit",
    category: "general",
    title: "",
    description: "",
    parent_notified: true,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.student_id || !form.title) return toast.error("Student and title required");
    setSaving(true);
    try {
      await api.logLifeEvent(form);
      toast.success("Event logged");
      onSaved();
      onClose();
    } catch { toast.error("Unable to save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="event-form-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">Log behaviour event</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="text-slate-600">Student</span>
            <select data-testid="input-event-student" value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded">
              {students.slice(0, 300).map((s) => (
                <option key={s.id} value={s.id}>{s.first_name} {s.last_name} · {s.year_group}</option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-slate-600">Kind</span>
              <select data-testid="input-event-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded">
                {KINDS.map((k) => <option key={k}>{k}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-slate-600">Category</span>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-slate-600">Title</span>
            <input data-testid="input-event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded" />
          </label>
          <label className="block">
            <span className="text-slate-600">Description</span>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded" />
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.parent_notified} onChange={(e) => setForm({ ...form, parent_notified: e.target.checked })} data-testid="input-parent-notified" />
            <span className="text-slate-700">Notify guardian</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="btn-save-event" className="hl-btn-accent px-4 py-2 text-sm">
            {saving ? "Saving…" : "Save event"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PastoralForm({ students, onClose, onSaved }) {
  const [form, setForm] = useState({ student_id: students[0]?.id || "", kind: "counselling", notes: "", confidential: true, follow_up_date: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.student_id || !form.notes) return toast.error("Student and notes required");
    setSaving(true);
    try {
      await api.addPastoral(form);
      toast.success("Pastoral note added");
      onSaved(); onClose();
    } catch { toast.error("Unable to save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="pastoral-form-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">Add pastoral note</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="text-slate-600">Student</span>
            <select value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded">
              {students.slice(0, 300).map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-slate-600">Kind</span>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded">
              {PASTORAL_KINDS.map((k) => <option key={k}>{k}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-slate-600">Notes (confidential)</span>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded" data-testid="input-pastoral-notes" />
          </label>
          <label className="block">
            <span className="text-slate-600">Follow up date</span>
            <input type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded" />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="btn-save-pastoral" className="hl-btn-accent px-4 py-2 text-sm">
            {saving ? "Saving…" : "Save note"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AwardForm({ students, onClose, onSaved }) {
  const [form, setForm] = useState({ student_id: students[0]?.id || "", category: "academic", title: "", citation: "", academic_year: "", term: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title) return toast.error("Title required");
    setSaving(true);
    try {
      await api.logLifeAward(form);
      toast.success("Award recorded");
      onSaved(); onClose();
    } catch { toast.error("Unable to save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="award-form-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">Record award</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="text-slate-600">Student</span>
            <select value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded">
              {students.slice(0, 300).map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-slate-600">Category</span>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded">
              {AWARD_CATEGORIES.map((k) => <option key={k}>{k}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-slate-600">Title</span>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded" data-testid="input-award-title" />
          </label>
          <label className="block">
            <span className="text-slate-600">Citation</span>
            <textarea value={form.citation} onChange={(e) => setForm({ ...form, citation: e.target.value })} rows={2} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded" />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="btn-save-award" className="hl-btn-accent px-4 py-2 text-sm">
            {saving ? "Saving…" : "Save award"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadershipForm({ students, onClose, onSaved }) {
  const [form, setForm] = useState({ student_id: students[0]?.id || "", role: "Prefect", scope: "School", academic_year: "2026" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.appointLeader(form);
      toast.success("Leadership role appointed");
      onSaved(); onClose();
    } catch { toast.error("Unable to save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="leadership-form-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">Appoint student leader</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="text-slate-600">Student</span>
            <select value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded">
              {students.slice(0, 300).map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-slate-600">Role</span>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded">
              {LEADERSHIP_ROLES.map((r) => <option key={r}>{r}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-slate-600">Scope (House or School)</span>
            <input value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded" />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="btn-save-leader" className="hl-btn-accent px-4 py-2 text-sm">
            {saving ? "Saving…" : "Appoint"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StudentLife360() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [kpis, setKpis] = useState(null);
  const [events, setEvents] = useState([]);
  const [activities, setActivities] = useState([]);
  const [awards, setAwards] = useState([]);
  const [leadership, setLeadership] = useState([]);
  const [pastoral, setPastoral] = useState([]);
  const [students, setStudents] = useState([]);
  const [modal, setModal] = useState(null);

  const load = async () => {
    try {
      const [k, ev, ac, aw, ld, ps, st] = await Promise.all([
        api.lifeKpis(),
        api.lifeEvents({ limit: 200 }),
        api.lifeActivities(),
        api.lifeAwards(),
        api.lifeLeadership(),
        api.lifePastoral(),
        api.students({ limit: 300 }),
      ]);
      setKpis(k.data);
      setEvents(ev.data || []);
      setActivities(ac.data || []);
      setAwards(aw.data || []);
      setLeadership(ld.data || []);
      setPastoral(ps.data || []);
      setStudents(st.data || []);
    } catch (e) {
      toast.error("Unable to load Student Life360");
    }
  };
  useEffect(() => { load(); }, []);

  const studentById = useMemo(() => {
    const m = {}; (students || []).forEach((s) => (m[s.id] = s)); return m;
  }, [students]);

  if (!kpis) return <Loading label="Loading Student Life360…" />;

  const tone = kpis.totals.merit_ratio >= 3 ? "on_target" : kpis.totals.merit_ratio >= 1.5 ? "monitor" : "attention";

  const renderTab = () => {
    if (tab === "overview") {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPITile testId="life-kpi-merits" label="Merits" value={kpis.totals.merits} sub={`ratio ${kpis.totals.merit_ratio || 0}`} />
            <KPITile testId="life-kpi-demerits" label="Demerits" value={kpis.totals.demerits} />
            <KPITile testId="life-kpi-awards" tone="dark" label="Awards" value={kpis.totals.awards} />
            <KPITile testId="life-kpi-pastoral" label="Pastoral open" value={kpis.totals.pastoral_open} sub={`${kpis.totals.leaders} leaders`} />
          </div>
          <Section title="House leaderboard" subtitle="Cumulative house points across all events">
            <div className="hl-card p-5 h-64" data-testid="house-leaderboard-chart">
              <ResponsiveContainer>
                <BarChart data={kpis.house_leaderboard} layout="vertical">
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                  <XAxis type="number" fontSize={11} />
                  <YAxis type="category" dataKey="house" width={110} fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="points" radius={[0, 4, 4, 0]}>
                    {kpis.house_leaderboard.map((h, i) => (
                      <Cell key={i} fill={["#D9531E", "#002147", "#059669", "#7c3aed"][i % 4]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>
          <Section title="Recent behaviour events">
            <div className="hl-card overflow-hidden">
              <table className="hl-table w-full text-sm" data-testid="recent-events-table">
                <thead><tr><th>Kind</th><th>Student</th><th>House</th><th>Title</th><th className="text-right">Points</th></tr></thead>
                <tbody>
                  {events.slice(0, 15).map((e) => {
                    const s = studentById[e.student_id];
                    return (
                      <tr key={e.id}>
                        <td><StatusPill status={KIND_TONE[e.kind] || "minor"} /></td>
                        <td>{s ? `${s.first_name} ${s.last_name}` : e.student_id.slice(0, 6)}</td>
                        <td>{e.house}</td>
                        <td className="text-xs">{e.title}</td>
                        <td className="text-right font-semibold" style={{ color: e.house_points >= 0 ? "#059669" : "#dc2626" }}>{e.house_points}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        </div>
      );
    }
    if (tab === "behaviour") {
      return (
        <div className="space-y-5">
          <Section
            title="Behaviour events"
            subtitle="Merits, demerits, commendations and reprimands. Every entry updates the house points ledger."
            actions={<button onClick={() => setModal("event")} data-testid="btn-add-event" className="hl-btn-accent inline-flex items-center gap-2 text-sm px-3 py-2"><Plus className="w-4 h-4" />Log event</button>}
          >
            <div className="hl-card overflow-hidden">
              <table className="hl-table w-full text-sm" data-testid="behaviour-events-table">
                <thead><tr><th>Kind</th><th>Student</th><th>House</th><th>Category</th><th>Title</th><th>Notified</th><th className="text-right">Points</th></tr></thead>
                <tbody>
                  {events.map((e) => {
                    const s = studentById[e.student_id];
                    return (
                      <tr key={e.id}>
                        <td><StatusPill status={KIND_TONE[e.kind] || "minor"} /></td>
                        <td>{s ? `${s.first_name} ${s.last_name}` : e.student_id.slice(0, 8)}</td>
                        <td>{e.house}</td>
                        <td className="text-xs">{e.category}</td>
                        <td className="text-xs max-w-xs truncate">{e.title}</td>
                        <td>{e.parent_notified ? <ShieldCheck className="w-4 h-4 text-emerald-600" /> : <span className="text-xs text-slate-400">—</span>}</td>
                        <td className="text-right font-semibold" style={{ color: e.house_points >= 0 ? "#059669" : "#dc2626" }}>{e.house_points}</td>
                      </tr>
                    );
                  })}
                  {!events.length && <tr><td colSpan={7} className="text-center py-6 text-slate-400">No behaviour events yet</td></tr>}
                </tbody>
              </table>
            </div>
          </Section>
        </div>
      );
    }
    if (tab === "pastoral") {
      return (
        <Section
          title="Pastoral & wellbeing"
          subtitle="Confidential counselling notes, wellness checks, and follow-ups"
          actions={<button onClick={() => setModal("pastoral")} data-testid="btn-add-pastoral" className="hl-btn-accent inline-flex items-center gap-2 text-sm px-3 py-2"><Plus className="w-4 h-4" />Add note</button>}
        >
          <div className="hl-card overflow-hidden">
            <table className="hl-table w-full text-sm" data-testid="pastoral-table">
              <thead><tr><th>Kind</th><th>Student</th><th>Notes</th><th>Follow up</th><th>By</th></tr></thead>
              <tbody>
                {pastoral.map((p) => {
                  const s = studentById[p.student_id];
                  return (
                    <tr key={p.id}>
                      <td><span className="text-xs uppercase font-semibold text-[color:var(--hl-orange)]">{p.kind.replace(/_/g, " ")}</span></td>
                      <td>{s ? `${s.first_name} ${s.last_name}` : "—"}</td>
                      <td className="text-xs max-w-md truncate">{p.notes}</td>
                      <td className="text-xs">{p.follow_up_date || "—"}</td>
                      <td className="text-xs text-slate-500">{p.recorded_by}</td>
                    </tr>
                  );
                })}
                {!pastoral.length && <tr><td colSpan={5} className="text-center py-6 text-slate-400">No pastoral notes yet</td></tr>}
              </tbody>
            </table>
          </div>
        </Section>
      );
    }
    if (tab === "activities") {
      return (
        <Section title="Clubs, societies & activities" subtitle="Sport, arts, STEM and service programmes">
          {activities.length === 0 ? (
            <EmptyState icon={Trophy} title="No activities yet" body="Seed data will populate after next server restart." />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="activities-grid">
              {activities.map((a) => (
                <div key={a.id} className="hl-card p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[color:var(--hl-orange)] font-semibold">{a.kind}</div>
                      <div className="font-display font-bold text-base mt-0.5">{a.name}</div>
                    </div>
                    <span className="text-xs text-slate-500">{(a.members || []).length} members</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-2 space-y-0.5">
                    <div>Coach · {a.coach || "TBD"}</div>
                    <div>{a.day} · {a.time} · {a.location}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      );
    }
    if (tab === "awards") {
      return (
        <Section
          title="Awards & recognitions"
          actions={<button onClick={() => setModal("award")} data-testid="btn-add-award" className="hl-btn-accent inline-flex items-center gap-2 text-sm px-3 py-2"><Plus className="w-4 h-4" />Record award</button>}
        >
          <div className="hl-card overflow-hidden">
            <table className="hl-table w-full text-sm" data-testid="awards-table">
              <thead><tr><th>Category</th><th>Title</th><th>Student</th><th>Term</th><th>Awarded</th></tr></thead>
              <tbody>
                {awards.map((aw) => {
                  const s = studentById[aw.student_id];
                  return (
                    <tr key={aw.id}>
                      <td><span className="text-xs uppercase font-semibold text-[color:var(--hl-orange)]">{aw.category}</span></td>
                      <td>{aw.title}</td>
                      <td>{s ? `${s.first_name} ${s.last_name}` : "—"}</td>
                      <td className="text-xs">{aw.term}</td>
                      <td className="text-xs text-slate-500">{(aw.awarded_at || "").slice(0, 10)}</td>
                    </tr>
                  );
                })}
                {!awards.length && <tr><td colSpan={5} className="text-center py-6 text-slate-400">No awards yet</td></tr>}
              </tbody>
            </table>
          </div>
        </Section>
      );
    }
    if (tab === "leadership") {
      return (
        <Section
          title="Student leadership"
          subtitle="Prefects, house captains, SRC and sports leaders"
          actions={<button onClick={() => setModal("leader")} data-testid="btn-add-leader" className="hl-btn-accent inline-flex items-center gap-2 text-sm px-3 py-2"><Plus className="w-4 h-4" />Appoint</button>}
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="leadership-grid">
            {leadership.map((l) => {
              const s = studentById[l.student_id];
              return (
                <div key={l.id} className="hl-card p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[color:var(--hl-orange)] font-semibold">{l.role}</div>
                      <div className="font-display font-bold text-base mt-0.5">{s ? `${s.first_name} ${s.last_name}` : l.student_id.slice(0, 8)}</div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 mt-2">Scope · {l.scope} · AY {l.academic_year}</div>
                </div>
              );
            })}
            {!leadership.length && <div className="col-span-full"><EmptyState icon={GraduationCap} title="No leaders appointed yet" /></div>}
          </div>
        </Section>
      );
    }
    return null;
  };

  return (
    <div data-testid="studentlife-page">
      <PageHeader
        kicker="Student Life360™"
        title="Behaviour, wellbeing & recognition"
        subtitle="A single record of every merit, demerit, house point, activity, award, leadership role and pastoral note. KPIs powered by the Central Metrics Service."
      />

      <div className="flex flex-wrap gap-2 mb-6" data-testid="life-tabs">
        <Tab id="overview" active={tab === "overview"} onClick={() => setTab("overview")} icon={Sparkles} label="Overview" />
        <Tab id="behaviour" active={tab === "behaviour"} onClick={() => setTab("behaviour")} icon={ShieldCheck} label="Behaviour" count={events.length} />
        <Tab id="pastoral" active={tab === "pastoral"} onClick={() => setTab("pastoral")} icon={HeartHandshake} label="Pastoral" count={pastoral.length} />
        <Tab id="activities" active={tab === "activities"} onClick={() => setTab("activities")} icon={Trophy} label="Activities" count={activities.length} />
        <Tab id="awards" active={tab === "awards"} onClick={() => setTab("awards")} icon={Award} label="Awards" count={awards.length} />
        <Tab id="leadership" active={tab === "leadership"} onClick={() => setTab("leadership")} icon={GraduationCap} label="Leadership" count={leadership.length} />
      </div>

      {renderTab()}

      {modal === "event" && <EventForm students={students} onClose={() => setModal(null)} onSaved={load} />}
      {modal === "pastoral" && <PastoralForm students={students} onClose={() => setModal(null)} onSaved={load} />}
      {modal === "award" && <AwardForm students={students} onClose={() => setModal(null)} onSaved={load} />}
      {modal === "leader" && <LeadershipForm students={students} onClose={() => setModal(null)} onSaved={load} />}
    </div>
  );
}
